import { Pressable, View, ActivityIndicator } from "react-native"
import MIcon from "@react-native-vector-icons/material-design-icons"
import * as Burnt from "burnt"
import { trpc } from "utils/trpc"
import { priceFromDBFormat, priceToDBFormat } from "@src/utils/price-utils"
import { ar } from "i18n/ar"
import { Text, TextInput } from "./core"
import { Controller, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "@white-labels/input"
import { ModalWrapper } from "./core"

const menuProductFormSchema = z.object({
  listedInApp: z.boolean(),
  isAvailable: z.boolean(),
  canExceedInventory: z.boolean(),
  maxOrderQuantity: z.optional(z.coerce.number().check(z.minimum(1))),
  price: z.coerce.number().check(z.minimum(0)),
  originalPrice: z.optional(z.coerce.number().check(z.minimum(0))),
})

type MenuProductEditModalProps = {
  onClose: () => void
  productId: number
  initialValues: {
    listedInApp: boolean
    isAvailable: boolean
    canExceedInventory: boolean
    maxOrderQuantity: number | null
    price: number
    originalPrice: number | null
    name: string // Only used for display
  }
  onSuccess?: () => void
}

function Checkbox({ value, onChange, label }: { value?: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <Pressable onPress={() => onChange(!value)} className="flex-row items-center justify-between py-3 border-b border-gray-100 active:opacity-70">
      <Text className="text-base">{label}</Text>
      <View className={`w-6 h-6 rounded border-2 items-center justify-center ${value ? "bg-blue-500 border-blue-500" : "bg-white border-gray-300"}`}>
        {value && <MIcon name="check" size={16} color="white" />}
      </View>
    </Pressable>
  )
}

export function MenuProductEditModal({ onClose, productId, initialValues, onSuccess }: MenuProductEditModalProps) {
  const utils = trpc.useUtils()
  const { control, handleSubmit } = useForm({
    resolver: zodResolver(menuProductFormSchema),
    defaultValues: {
      listedInApp: initialValues.listedInApp,
      isAvailable: initialValues.isAvailable,
      canExceedInventory: initialValues.canExceedInventory,
      maxOrderQuantity: initialValues.maxOrderQuantity ?? undefined,
      price: priceFromDBFormat(initialValues.price),
      originalPrice: initialValues.originalPrice ? priceFromDBFormat(initialValues.originalPrice) : undefined,
    },
  })

  const editMenuSettings = trpc.products.editMenuSettings.useMutation()

  const onSubmit = handleSubmit((data) => {
    editMenuSettings.mutate(
      {
        id: productId,
        price: priceToDBFormat(data.price),
        originalPrice: data.originalPrice ? priceToDBFormat(data.originalPrice) : undefined,
        listedInApp: data.listedInApp,
        isAvailable: data.isAvailable,
        canExceedInventory: data.canExceedInventory,
        maxOrderQuantity: data.maxOrderQuantity,
      },
      {
        onSuccess: () => {
          Burnt.toast({ title: ar.success, preset: "done" })
          utils.menu.collection.invalidate()
          onSuccess?.()
          onClose()
        },
        onError: (error) => {
          Burnt.toast({ title: error.message, preset: "error" })
        },
      },
    )
  })

  const isPending = editMenuSettings.isPending

  return (
    <ModalWrapper onClose={onClose}>
      <View className="p-4 gap-4">
        <Text className="text-xl font-bold">{ar.editMenuProduct}</Text>
        <Text className="text-gray-500">{initialValues.name}</Text>

        {/* Listed in App Checkbox */}
        <Controller
          control={control}
          name="listedInApp"
          render={({ field: { onChange, value } }) => <Checkbox value={value} onChange={onChange} label={ar.listedInApp} />}
        />

        {/* Available for Sale Checkbox */}
        <Controller
          control={control}
          name="isAvailable"
          render={({ field: { onChange, value } }) => <Checkbox value={value} onChange={onChange} label={ar.availableForSale} />}
        />

        {/* Can Exceed Inventory Checkbox */}
        <Controller
          control={control}
          name="canExceedInventory"
          render={({ field: { onChange, value } }) => <Checkbox value={value} onChange={onChange} label={ar.canExceedInventory} />}
        />

        {/* Max Quantity Input */}
        <Controller
          control={control}
          name="maxOrderQuantity"
          render={({ field: { onChange, value } }) => (
            <View className="gap-1">
              <Text className="text-sm text-gray-600">{ar.maxQuantityPerOrder}</Text>
              <TextInput
                placeholder={ar.optional}
                value={value?.toString() ?? ""}
                onChangeText={(text) => onChange(text ? Number(text) : undefined)}
                keyboardType="numeric"
              />
            </View>
          )}
        />

        {/* Price Input */}
        <Controller
          control={control}
          name="price"
          render={({ field: { onChange, value } }) => (
            <View className="gap-1">
              <Text className="text-sm text-gray-600">{ar.price}</Text>
              <TextInput
                placeholder={ar.price}
                value={value?.toString() ?? ""}
                onChangeText={(text) => onChange(text ? Number(text) : 0)}
                keyboardType="numeric"
              />
            </View>
          )}
        />

        {/* Original Price Input */}
        <Controller
          control={control}
          name="originalPrice"
          render={({ field: { onChange, value } }) => (
            <View className="gap-1">
              <Text className="text-sm text-gray-600">{ar.originalPrice}</Text>
              <TextInput
                placeholder={ar.optional}
                value={value?.toString() ?? ""}
                onChangeText={(text) => onChange(text ? Number(text) : undefined)}
                keyboardType="numeric"
              />
            </View>
          )}
        />

        {/* Submit Button */}
        <Pressable className="bg-blue-500 p-4 rounded items-center active:opacity-70" onPress={onSubmit} disabled={isPending}>
          {isPending ? <ActivityIndicator color="white" /> : <Text className="text-white font-bold">{ar.save}</Text>}
        </Pressable>
      </View>
    </ModalWrapper>
  )
}
